import React, { useState } from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { OfertaResponse, UpdateOfertaRequest } from '../../../../store/MSCommerce/Ofertas/types';
import styles from '../Ofertas.module.scss';

interface UpdateOfertaModalProps {
  oferta: OfertaResponse;
  onClose: () => void;
  onSubmit: (data: UpdateOfertaRequest) => void;
}

const toDateInput = (d: Date | null): string => {
  if (!d) return '';
  return new Date(d).toISOString().slice(0, 10);
};

export const UpdateOfertaModal: React.FC<UpdateOfertaModalProps> = ({ oferta, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    nombre: oferta.nombre ?? '',
    valor: String(oferta.valor ?? ''),
    fechaInicio: toDateInput(oferta.fechaInicio),
    fechaFin: toDateInput(oferta.fechaFin),
    status: oferta.status ?? 'ACTIVO',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const field = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es requerido';
    if (oferta.tipo !== '2x1' && (!form.valor || isNaN(Number(form.valor)) || Number(form.valor) <= 0)) {
      e.valor = 'Ingresa un valor válido';
    }
    if (form.fechaInicio && form.fechaFin && form.fechaFin < form.fechaInicio) {
      e.fechaFin = 'Debe ser posterior a la fecha de inicio';
    }
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    onSubmit({
      id: oferta.id,
      nombre: form.nombre,
      valor: oferta.tipo === '2x1' ? 0 : Number(form.valor),
      fechaInicio: form.fechaInicio ? new Date(form.fechaInicio) : null,
      fechaFin: form.fechaFin ? new Date(form.fechaFin) : null,
      status: form.status,
      detallesNuevos: null,
    });
  };

  return (
    <div className={styles.modal__overlay} onClick={onClose}>
      <div className={styles.modal__panel} onClick={e => e.stopPropagation()}>

        <div className={styles.modal__header}>
          <div className={styles.modal__headerLeft}>
            <h2 className={styles.modal__title}>Editar Oferta</h2>
          </div>
          <button className={styles.modal__close} onClick={onClose}><CloseOutlined /></button>
        </div>

        <div className={styles.modal__body}>
          <div className={styles.modal__grid}>

            <div className={`${styles.modal__field} ${styles['modal__field--full']}`}>
              <label className={styles.modal__label}>Nombre de la oferta *</label>
              <input
                className={`${styles.modal__input} ${errors.nombre ? styles['modal__input--error'] : ''}`}
                value={form.nombre}
                onChange={e => field('nombre', e.target.value)}
              />
              {errors.nombre && <span className={styles.modal__error}>{errors.nombre}</span>}
            </div>

            {oferta.tipo !== '2x1' && (
              <div className={styles.modal__field}>
                <label className={styles.modal__label}>
                  Valor * {oferta.tipo === 'PORCENTAJE' ? '(%)' : '($)'}
                </label>
                <input
                  className={`${styles.modal__input} ${errors.valor ? styles['modal__input--error'] : ''}`}
                  type="number"
                  min="0"
                  value={form.valor}
                  onChange={e => field('valor', e.target.value)}
                />
                {errors.valor && <span className={styles.modal__error}>{errors.valor}</span>}
              </div>
            )}

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Fecha de inicio</label>
              <input
                className={styles.modal__input}
                type="date"
                value={form.fechaInicio}
                onChange={e => field('fechaInicio', e.target.value)}
              />
            </div>

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Fecha de fin</label>
              <input
                className={`${styles.modal__input} ${errors.fechaFin ? styles['modal__input--error'] : ''}`}
                type="date"
                value={form.fechaFin}
                onChange={e => field('fechaFin', e.target.value)}
              />
              {errors.fechaFin && <span className={styles.modal__error}>{errors.fechaFin}</span>}
            </div>

            <div className={`${styles.modal__field} ${styles['modal__field--full']}`}>
              <label className={styles.modal__label}>Estado</label>
              <div className={styles.modal__statusGroup}>
                {['ACTIVO', 'INACTIVO', 'EXPIRADO'].map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`${styles.modal__statusBtn} ${form.status === s ? styles['modal__statusBtn--active'] : ''}`}
                    onClick={() => field('status', s)}
                  >
                    {s === 'ACTIVO' ? 'Activa' : s === 'INACTIVO' ? 'Inactiva' : 'Expirada'}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>

        <div className={styles.modal__footer}>
          <button className={styles.modal__cancel} onClick={onClose}>Cancelar</button>
          <button className={styles.modal__submit} onClick={handleSubmit}>
            <CheckOutlined /> Guardar Cambios
          </button>
        </div>

      </div>
    </div>
  );
};
